import { useState } from "react";

interface CharacterImageProps {
  name: string;
  size?: number;
  className?: string;
}

// Build-time stamp injected by Vite (see vite.config.ts `define`). Used to
// cache-bust the fixed-path /characters/<Name>.png URLs so a phone that
// long-cached an earlier deploy's avatar (back when CDK shipped them with
// immutable + max-age=1y) picks the new copy up on first refresh after a
// version bump.
const ASSET_VERSION =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";

/**
 * Renders the assigned student's Binogi character image. The asset lives at
 * /characters/<Name>.png and is generated at build-time by
 * `scripts/fetch-character-images.mjs`. Falls back gracefully to a coloured
 * initial badge if the image is missing (e.g. the rare " 2" collision name).
 */
export function CharacterImage({
  name,
  size = 160,
  className,
}: CharacterImageProps) {
  const baseName = name.replace(/ 2$/, "").trim();
  const safeName = baseName.replace(/[^A-Za-z]/g, "");
  const [errored, setErrored] = useState(false);

  if (!safeName || errored) {
    const initial = name.charAt(0).toUpperCase();
    return (
      <div
        className={`rounded-full bg-brand-purple/40 text-white flex items-center justify-center font-bold ${className ?? ""}`}
        style={{ width: size, height: size, fontSize: size * 0.45 }}
      >
        {initial}
      </div>
    );
  }

  return (
    <img
      src={`/characters/${safeName}.png?v=${ASSET_VERSION}`}
      alt={name}
      width={size}
      height={size}
      onError={() => setErrored(true)}
      className={`object-contain ${className ?? ""}`}
      style={{ width: size, height: size }}
      draggable={false}
    />
  );
}
